import { getAttributes, logUiError } from '@/services/attributeLambda';
import { ATTRIBUTE_TYPES, CONTACT_ROLES, REPORT_STATUSES } from '@/utils/constants';
import { defineStore } from 'pinia';
import { ref, reactive } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from './authStore';
import SecureLS from 'secure-ls';

const ls = new SecureLS({ encodingType: 'aes' });

export const useAppStore = defineStore('appStore', () => {

  const attributes = ref([]);
  const pageFilters = ref({});
  const route = useRoute();
  const loadingState = reactive({
    loading: false,
    loadingMessage: ''
  });

  const errorState = reactive({
    hasError: false,
    errorMessage: ''
  });

  const authStore = useAuthStore();

  function setPageFilter(key, value) {
    if (key && value !== undefined) {
      pageFilters.value[key] = value;
    }
  }

  function getPageFilter(key) {
    return key ? pageFilters.value[key] : null;
  }

  function clearPageFilter(key) {
    if (key) {
      delete pageFilters.value[key];
    } else {
      pageFilters.value = {};
    }
  }
  
  function clearAllPageFilters() {
    pageFilters.value = {};
  }

  function logError(error) {
    if (!error) return;
    if (authStore.authenticated) {
      logUiError({ message: error.message, stack: error.stack, route: route.fullPath, level: 'error' });
    }
  }

  async function initializeStore() {
    try {
      loadingState.loading = false;
      loadingState.loadingMessage = '';
      if (!attributes.value || attributes.value.length === 0) {
        attributes.value = await getAttributes();
      }
    } catch (error) {
      console.error('Error initializing app store:', error);
      logError(error);
    }
  }

  function getAttributeValues(attributeType) {
    return attributes.value.filter(attribute => attribute.attributeTypeDesc === attributeType);
  }

  function getAttributeDescriptionById(attributeId) {
    return attributes.value.find(attribute => attribute.attributeId === attributeId)?.attributeTypeDesc;
  }

  function getAttributeTextValue1ById(attributeId) {
    return attributes.value.find(attribute => attribute.attributeId === attributeId)?.textValue1;
  }

  function getAttributeIdByTypeAndTextValue1(attributeType, textValue1) {
    const id = attributes.value.find(attribute => attribute.attributeTypeDesc === attributeType && attribute.textValue1 === textValue1)?.attributeId;
    return id;
  }

  function getHpContactRoles(isTpaUser = false) {
    const roles = getAttributeValues(ATTRIBUTE_TYPES.ContactRole);
    return authStore.user.isTpaUser || isTpaUser ?
      roles.filter(role => role.numericValue1 === 1 && role.numericValue2 === 0) :
      roles.filter(role => role.numericValue2 === 0);
  }

  function getFoscContactRoles(isTpaUser = false) {
    const roles = getAttributeValues(ATTRIBUTE_TYPES.ContactRole);
    const sortedRoles = (authStore.user.isTpaUser || isTpaUser
      ? roles.filter(role => role.numericValue1 === 1)
      : roles
    ).sort((a, b) => {
      const sortOrderA = Object.values(CONTACT_ROLES).find(r => r.name === a.textValue1)?.sortId || Infinity;
      const sortOrderB = Object.values(CONTACT_ROLES).find(r => r.name === b.textValue1)?.sortId || Infinity;
      return sortOrderA - sortOrderB;
    });
    return sortedRoles;
  }

  function getDIContactRoles(isTpaUser = false) {
    const roles = getAttributeValues(ATTRIBUTE_TYPES.ContactRole);
    const sortedRoles = (authStore.user.isTpaUser || isTpaUser
      ? roles.filter(role => role.numericValue1 === 1)
      : roles
    ).sort((a, b) => {
      const sortOrderA = Object.values(CONTACT_ROLES).find(r => r.name === a.textValue1)?.sortId || Infinity;
      const sortOrderB = Object.values(CONTACT_ROLES).find(r => r.name === b.textValue1)?.sortId || Infinity;
      return sortOrderA - sortOrderB;
    });
    return sortedRoles;
  }

  function getCexContactRoles(isTpaUser = false) {
    const roles = getAttributeValues(ATTRIBUTE_TYPES.ContactRole);
    return (authStore.user.isTpaUser || isTpaUser)
      ? roles.filter(role => role.numericValue1 === 1)
      : roles;
  }

  function getUroContactRoles(isTpaUser = false) {
    const roles = getAttributeValues(ATTRIBUTE_TYPES.ContactRole);
    return (authStore.user.isTpaUser || isTpaUser)
      ? roles.filter(role => role.numericValue1 === 1 && role.numericValue2 === 0)
      : roles.filter(role => role.numericValue2 === 0);
  }

  function getAffiliationTypes(affiliationType) {
    const types =  getAttributeValues(ATTRIBUTE_TYPES.Affiliation).filter(
      affiliation => affiliation.textValue2 === affiliationType
    ).sort((a, b) => a.numericValue1 - b.numericValue1);

    return types;
  }

  function hceContactRoles() {
    const roles = getAttributeValues(ATTRIBUTE_TYPES.ContactRole);
    return roles.filter(role => role.numericValue2 !== 1);
  }

/**
 * Gets the section ID for a given attribute type description
 * @param {string} attributeTypeSectionDescription - The description of the section attribute type
 * @returns {number|null} The section ID if valid, null otherwise
 */
function getSectionId(attributeTypeSectionDescription, sectionName) {
  if (!attributeTypeSectionDescription || !sectionName) {
    throw new Error('Both attributeTypeSectionDescription and sectionName are required.');
  }
  
  // Find the matching attribute type (case-insensitive)
  const matchingType = Object.values(ATTRIBUTE_TYPES).find(
    type => type.toLowerCase() === attributeTypeSectionDescription.toLowerCase()
  );
  
  if (!matchingType) {
    console.warn(`Invalid attribute type description: ${attributeTypeSectionDescription}`);
    return null;
  }

  return attributes.value.find(attribute => attribute.attributeTypeDesc === matchingType && attribute.textValue1 === sectionName)?.attributeId; 
}

  function getHpSectionId(sectionName) {
    return attributes.value.find(attribute => attribute.attributeTypeDesc === ATTRIBUTE_TYPES.HpSection && attribute.textValue1 === sectionName)?.attributeId;
  }

  function getFoscSectionId(sectionName) {
    const id =  attributes.value.find(attribute => attribute.attributeTypeDesc === ATTRIBUTE_TYPES.FoscSection && attribute.textValue1 === sectionName)?.
      attributeId;
    return id;
  }

  function getCexSectionId(sectionName) {
    const id =  attributes.value.find(attribute => attribute.attributeTypeDesc === ATTRIBUTE_TYPES.CexSection && attribute.textValue1 === sectionName)?.
      attributeId;
    return id;
  }

  function getDocumentTypes(textValue2) {
    const attribs = getAttributeValues(ATTRIBUTE_TYPES.Document);
    return attribs.filter(attrib => attrib.textValue2 === textValue2);
  }

  function getHceDocumentTypes() {
    return getDocumentTypes('HCET');
  }

  function getFoscDocumentTypes() {
    return getDocumentTypes('FOSC');
  }

  function getDiDocumentTypes() {
    return getDocumentTypes('DI');
  }
  
  function getProgramReportStatuses() {
    const attribs = getAttributeValues(ATTRIBUTE_TYPES.ReportStatus)
      .filter(x => x.textValue1 != REPORT_STATUSES.Pending)
      .sort((a, b) => a.numericValue1 - b.numericValue1);

    return attribs;
  }

  function showLoading(message = "Loading...") {
    loadingState.loading = true;
    loadingState.loadingMessage = message;
  }

  function hideLoading() {
    loadingState.loading = false;
    loadingState.loadingMessage = '';
  }

  function showError(message = "An error occurred.") {
    errorState.hasError = true;
    errorState.errorMessage = message;
  }

  function hideError() {
    errorState.hasError = false;
    errorState.errorMessage = '';
  }

  function resetState() {
    hideLoading();
    hideError();
    clearAllPageFilters();
  }

  return {
    attributes,
    pageFilters,
    loadingState,
    errorState, 

    initializeStore,
    logError,
    getAttributeValues,
    hpContactRoles: getHpContactRoles,
    hceContactRoles,
    getHpSectionId,
    getFoscSectionId,
    getCexSectionId,
    getAttributeDescriptionById,
    getAttributeTextValue1ById,
    getAttributeIdByTypeAndTextValue1,
    hceDocumentTypes: getHceDocumentTypes,
    foscDocumentTypes: getFoscDocumentTypes,
    diDocumentTypes: getDiDocumentTypes,
    getDocumentTypes,
    foscContactRoles: getFoscContactRoles,
    diContactRoles: getDIContactRoles,
    cexContactRoles: getCexContactRoles,
    uroContactRoles: getUroContactRoles,
    affiliationTypes: getAffiliationTypes,
    getSectionId,
    getProgramReportStatuses,
    setPageFilter,
    getPageFilter,
    clearPageFilter,
    clearAllPageFilters,
    showLoading,
    hideLoading,
    showError,
    hideError,
    resetState,
  };
},
{
  persist: {
    storage: {
      getItem: key => {
        const data = ls.get(key);
        // SecureLS returns null if not found
        return typeof data === 'string' ? data : JSON.stringify(data);
      },
      setItem: (key, value) => {
        ls.set(key, value);
      },
      removeItem: key => {
        ls.remove(key);
      }
    }
  }
});